import { useState, useEffect, useRef } from "react";

function MaskedInput(props) {
  const {
    value,
    onChange,
    formatRegex,
    placeholder,
    className,
    disabled
  } = props;

  const [inputValue, setInputValue] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [formatSegments, setFormatSegments] = useState([]);

  const inputRef = useRef(null);
  const cursorPositionRef = useRef(null);

  // Parse regex to extract format structure
  useEffect(() => {
    if (formatRegex) {
      const segments = parseRegex(formatRegex);
      setFormatSegments(segments);
    }
  }, [formatRegex]);

  // Initialize from prop value
  useEffect(() => {
    setInputValue(value || "");
    setIsValid(true);
  }, [value]);

  // Restore cursor position after state update
  useEffect(() => {
    if (cursorPositionRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(
        cursorPositionRef.current,
        cursorPositionRef.current
      );
      cursorPositionRef.current = null;
    }
  }, [inputValue]);

  // Parse regex into format segments
  function parseRegex(regex) {
    if (!regex) return [];

    // Remove anchors
    let pattern = regex.replace(/^\^/, '').replace(/\$$/, '');

    const segments = [];
    let i = 0;

    while (i < pattern.length) {
      // Match \d{n}
      const digitMatch = pattern.substring(i).match(/^\\d\{(\d+)\}/);
      if (digitMatch) {
        segments.push({ type: 'digit', count: parseInt(digitMatch[1]) });
        i += digitMatch[0].length;
        continue;
      }

      // Match [a-zA-Z]{n}
      const alphaMatch = pattern.substring(i).match(/^\[a-zA-Z\]\{(\d+)\}/);
      if (alphaMatch) {
        segments.push({ type: 'alpha', count: parseInt(alphaMatch[1]) });
        i += alphaMatch[0].length;
        continue;
      }

      // Match [A-Z]{n}
      const upperMatch = pattern.substring(i).match(/^\[A-Z\]\{(\d+)\}/);
      if (upperMatch) {
        segments.push({ type: 'upper', count: parseInt(upperMatch[1]) });
        i += upperMatch[0].length;
        continue;
      }

      // Match [a-z]{n}
      const lowerMatch = pattern.substring(i).match(/^\[a-z\]\{(\d+)\}/);
      if (lowerMatch) {
        segments.push({ type: 'lower', count: parseInt(lowerMatch[1]) });
        i += lowerMatch[0].length;
        continue;
      }

      // Match [0-9a-zA-Z]{n} or [a-zA-Z0-9]{n}
      const alphanumMatch = pattern.substring(i).match(/^\[[0-9a-zA-Z]+\]\{(\d+)\}/);
      if (alphanumMatch) {
        segments.push({ type: 'alphanumeric', count: parseInt(alphanumMatch[1]) });
        i += alphanumMatch[0].length;
        continue;
      }

      // Literal character (separator)
      segments.push({ type: 'literal', char: pattern[i] });
      i++;
    }

    return segments;
  }

  // Check if character is valid for segment type
  function isValidChar(char, segmentType) {
    switch (segmentType) {
      case 'digit':
        return /\d/.test(char);
      case 'alpha':
        return /[a-zA-Z]/.test(char);
      case 'upper':
        return /[A-Z]/.test(char);
      case 'lower':
        return /[a-z]/.test(char);
      case 'alphanumeric':
        return /[a-zA-Z0-9]/.test(char);
      default:
        return false;
    }
  }

  // Extract valid characters based on format segments
  function extractValidChars(value) {
    let result = "";

    for (let i = 0; i < value.length; i++) {
      const char = value[i];
      // Check if this character is valid for any non-literal segment
      for (let j = 0; j < formatSegments.length; j++) {
        const segment = formatSegments[j];
        if (segment.type !== 'literal' && isValidChar(char, segment.type)) {
          result += char;
          break;
        }
      }
    }

    return result;
  }

  // Apply format structure to value
  function applyFormat(value) {
    if (formatSegments.length === 0) return value;

    const validChars = extractValidChars(value);
    let result = "";
    let charIndex = 0;

    for (let i = 0; i < formatSegments.length; i++) {
      const segment = formatSegments[i];

      if (segment.type === 'literal') {
        // Add literal character
        result += segment.char;
      } else {
        // Add characters of this type up to count
        let added = 0;
        while (charIndex < validChars.length && added < segment.count) {
          const char = validChars[charIndex];
          if (isValidChar(char, segment.type)) {
            result += char;
            charIndex++;
            added++;
          } else {
            charIndex++;
          }
        }

        // If we don't have enough characters for this segment, stop
        if (added < segment.count) {
          break;
        }
      }
    }

    return result;
  }

  // Validate value against regex
  function validateValue(val) {
    if (!formatRegex) return true;

    try {
      const regex = new RegExp(formatRegex);
      return regex.test(val);
    } catch (e) {
      console.error("Invalid validation regex:", e);
      return true;
    }
  }

  // Count valid characters before a position
  function countValidCharsBefore(value, position) {
    let count = 0;
    for (let i = 0; i < Math.min(value.length, position); i++) {
      const char = value[i];
      for (let j = 0; j < formatSegments.length; j++) {
        const segment = formatSegments[j];
        if (segment.type !== 'literal' && isValidChar(char, segment.type)) {
          count++;
          break;
        }
      }
    }
    return count;
  }

  // Calculate cursor position after formatting
  function calculateCursorPosition(formattedValue, targetValidChars) {
    let validCount = 0;

    for (let i = 0; i < formattedValue.length; i++) {
      const char = formattedValue[i];

      // Check if this is a valid character
      let isValid = false;
      for (let j = 0; j < formatSegments.length; j++) {
        const segment = formatSegments[j];
        if (segment.type !== 'literal' && isValidChar(char, segment.type)) {
          isValid = true;
          break;
        }
      }

      if (isValid) {
        validCount++;
      }

      if (validCount >= targetValidChars) {
        return i + 1;
      }
    }

    return formattedValue.length;
  }

  function handleInputChange(e) {
    const rawValue = e.target.value;
    const cursorPos = e.target.selectionStart;

    // Count valid characters before cursor in old value
    const oldValidChars = countValidCharsBefore(inputValue, cursorPos);

    // Detect if a character was added
    const oldTotalValidChars = extractValidChars(inputValue).length;
    const newTotalValidChars = extractValidChars(rawValue).length;
    const characterAdded = newTotalValidChars > oldTotalValidChars;

    // Apply formatting
    const formattedValue = applyFormat(rawValue);

    // Validate
    const valid = validateValue(formattedValue);
    setIsValid(valid);

    // Calculate new cursor position
    const targetValidChars = characterAdded ? oldValidChars + 1 : oldValidChars;
    const newCursorPos = calculateCursorPosition(formattedValue, targetValidChars);

    cursorPositionRef.current = newCursorPos;
    setInputValue(formattedValue);

    // Notify parent
    if (onChange) {
      onChange(formattedValue);
    }
  }

  function handleBlur() {
    // Validate on blur - don't reset, just show error state
    const valid = validateValue(inputValue);
    setIsValid(valid);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  }

  // Generate placeholder from format regex if not provided
  function getPlaceholder() {
    if (placeholder) return placeholder;

    if (formatSegments.length > 0) {
      let result = "";
      for (let i = 0; i < formatSegments.length; i++) {
        const segment = formatSegments[i];
        if (segment.type === 'literal') {
          result += segment.char;
        } else if (segment.type === 'digit') {
          result += '0'.repeat(segment.count);
        } else if (segment.type === 'alpha' || segment.type === 'alphanumeric') {
          result += 'X'.repeat(segment.count);
        } else if (segment.type === 'upper') {
          result += 'A'.repeat(segment.count);
        } else if (segment.type === 'lower') {
          result += 'a'.repeat(segment.count);
        }
      }
      return result;
    }

    return "";
  }

  return (
    <input
      ref={inputRef}
      type="text"
      className={className + (isValid ? "" : " error")}
      value={inputValue}
      onChange={handleInputChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      placeholder={getPlaceholder()}
      disabled={disabled}
    />
  );
}

export default MaskedInput;
